// Combo count calculation per hand category given exposed board cards

import { type Card, RANKS, SUITS } from './cards.js';
import { MATRIX_CELLS, type HandCategory, type MatrixCell } from './matrix.js';

export interface ComboCount {
  category: HandCategory;
  total: number;      // max combos for this category type (6/4/12)
  remaining: number;  // combos remaining given exposed cards
}

/** All specific two-card combos for a hand category */
function getCombos(cell: MatrixCell): [Card, Card][] {
  const combos: [Card, Card][] = [];
  const { isPair, isSuited } = cell;

  if (isPair) {
    // e.g. AA: C(4,2) = 6 combos
    const rank = RANKS[cell.row];
    for (let i = 0; i < SUITS.length; i++) {
      for (let j = i + 1; j < SUITS.length; j++) {
        combos.push(
          [{ rank, suit: SUITS[i] }, { rank, suit: SUITS[j] }]
        );
      }
    }
  } else if (isSuited) {
    // e.g. AKs: 4 combos (one per suit)
    const highRank = RANKS[cell.row];
    const lowRank = RANKS[cell.col];
    for (const suit of SUITS) {
      combos.push([{ rank: highRank, suit }, { rank: lowRank, suit }]);
    }
  } else {
    // offsuit: 12 combos (4x3 different suit combos)
    const highRank = RANKS[cell.col]; // col > row for offsuit
    const lowRank = RANKS[cell.row];
    for (const s1 of SUITS) {
      for (const s2 of SUITS) {
        if (s1 !== s2) {
          combos.push([{ rank: highRank, suit: s1 }, { rank: lowRank, suit: s2 }]);
        }
      }
    }
  }
  return combos;
}

/** Build a set of "rank+suit" strings for fast membership checks */
function buildExposedSet(boardCards: Card[]): Set<string> {
  return new Set(boardCards.map(c => `${c.rank}${c.suit}`));
}

/**
 * Compute combo counts for all 169 hand categories given the currently exposed board cards.
 * Returns a flat array of 169 counts in matrix order (row-major).
 */
export function computeAllComboCounts(boardCards: Card[]): number[] {
  const exposed = buildExposedSet(boardCards);
  return MATRIX_CELLS.map(cell => {
    const combos = getCombos(cell);
    let remaining = 0;
    for (const [c1, c2] of combos) {
      const k1 = `${c1.rank}${c1.suit}`;
      const k2 = `${c2.rank}${c2.suit}`;
      if (!exposed.has(k1) && !exposed.has(k2)) remaining++;
    }
    return remaining;
  });
}

/**
 * Compute the combo count for a single category.
 */
export function computeComboCount(category: HandCategory, boardCards: Card[]): number {
  const idx = MATRIX_CELLS.findIndex(c => c.category === category);
  if (idx === -1) return 0;
  const exposed = buildExposedSet(boardCards);
  const combos = getCombos(MATRIX_CELLS[idx]);
  return combos.filter(([c1, c2]) => 
    !exposed.has(`${c1.rank}${c1.suit}`) && !exposed.has(`${c2.rank}${c2.suit}`)
  ).length;
}
